import type { HttpRequest, NetworkErrorCode } from "../types.ts";

export const BRIDGE_PROTOCOL = "yomiri-bridge" as const;
export const BRIDGE_PROTOCOL_VERSION = 1 as const;

// Only serializable fields cross chrome.runtime messaging. Cache stays in the web client.
export type BridgeFetchRequest = Omit<HttpRequest, "signal" | "cache">;

export type BridgeCommand =
  | { type: "PING" }
  | { type: "UNREGISTER_SOURCE"; sourceId: string }
  | {
      type: "REGISTER_SOURCE";
      sourceId: string;
      revision: string;
      allowedHosts: string[];
      baseUrl?: string;
    }
  | { type: "FETCH"; request: BridgeFetchRequest }
  | { type: "SOURCE_TAB_IMAGE"; sourceId: string; url: string; referer: string }
  | { type: "CANCEL"; targetRequestId: string }
  | { type: "PREPARE_IMAGES"; sourceId: string; urls: string[]; referer: string };

export interface BridgeMessage {
  protocol: typeof BRIDGE_PROTOCOL;
  version: typeof BRIDGE_PROTOCOL_VERSION;
  requestId: string;
  command: BridgeCommand;
}

export type BridgeResponseBody =
  | { encoding: "text"; value: string }
  | { encoding: "base64"; value: string };

export interface BridgeFetchResult {
  status: number;
  finalUrl: string;
  headers: Record<string, string>;
  body: BridgeResponseBody;
  classification: "content" | "challenge";
}

export interface BridgeCommandResults {
  UNREGISTER_SOURCE: { removed: boolean };
  PING: {
    extensionVersion: string;
    capabilities: Array<
      "fetch" | "image-session-rules" | "cancel" | "source-registry" | "dynamic-hosts" | "source-tab-images"
    >;
  };
  REGISTER_SOURCE: { sourceId: string; revision: string };
  FETCH: BridgeFetchResult;
  SOURCE_TAB_IMAGE: BridgeFetchResult;
  CANCEL: { cancelled: boolean };
  PREPARE_IMAGES: { prepared: number };
}

export type BridgeReply<K extends BridgeCommand["type"]> = {
  protocol: typeof BRIDGE_PROTOCOL;
  version: typeof BRIDGE_PROTOCOL_VERSION;
  requestId: string;
} & (
  | { ok: true; data: BridgeCommandResults[K] }
  | { ok: false; error: { code: NetworkErrorCode; message: string; status?: number } }
);
